import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="chat-taxonomy-bulk-upload"
//
// Submits a ZIP file to the per-sub-context bulk-upload endpoint via fetch,
// because the workbench partial lives inside Trestle's outer resource form
// and HTML doesn't allow nested forms — a normal form_with would silently
// surrender the submission to the parent form's `update` action.
//
// Targets:
//   - fileInput:    <input type="file"> for the ZIP
//   - localeSelect: <select> for de/en
//   - status:       inline status text
//
// Values:
//   - url: bulk_upload endpoint (POST, accepts multipart/form-data)
export default class extends Controller {
  static targets = ['fileInput', 'localeSelect', 'status'];
  static values = { url: String };

  async upload(event) {
    event.preventDefault();
    event.stopPropagation();

    // Belt-and-braces guard against double submission. Even with the
    // Stimulus singleton fix in place, a fast double-tap (or any future
    // double-binding regression) must not race the importer — the unique
    // index on (relative_path, locale) raises PG::UniqueViolation when
    // two parallel uploads insert the same leaf.
    if (this.uploading) return;

    const file = this.fileInputTarget.files?.[0];
    if (!file) {
      this.setStatus('Bitte eine ZIP-Datei auswählen.', 'warning');
      return;
    }

    this.uploading = true;
    const button = event.currentTarget;
    button.disabled = true;
    this.setStatus('Lade hoch…', 'info');

    const data = new FormData();
    data.append('zip_file', file);
    data.append('locale', this.localeSelectTarget?.value || 'de');

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const response = await fetch(this.urlValue, {
        method: 'POST',
        body: data,
        headers: {
          'X-CSRF-Token': csrfToken || '',
          Accept: 'text/html',
        },
        credentials: 'same-origin',
      });

      if (response.ok || response.redirected) {
        this.setStatus('Hochgeladen — Seite wird neu geladen…', 'success');
        setTimeout(() => window.location.reload(), 400);
      } else {
        this.setStatus(`Upload fehlgeschlagen (HTTP ${response.status}).`, 'error');
        this.uploading = false;
        button.disabled = false;
      }
    } catch (err) {
      this.setStatus(`Fehler: ${err.message}`, 'error');
      this.uploading = false;
      button.disabled = false;
    }
  }

  setStatus(message, kind) {
    if (!this.hasStatusTarget) return;
    const colors = { info: '#0c5460', success: '#155724', warning: '#856404', error: '#721c24' };
    this.statusTarget.style.color = colors[kind] || '#333';
    this.statusTarget.textContent = message;
  }
}
